import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Grid } from '@mui/material'
import { styled } from '@mui/styles'
import * as R from 'ramda'
import { Text, Utils } from '@pbt/pbt-ui-components'

import ScrollAnchor from '~/components/common/ScrollAnchor'
import {
  CONVERSATION_MESSAGES_FIRST_FETCH_COUNT,
  MessageVariant,
  MessageWay,
} from '~/constants/communications'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { NotificationAreaNames } from '~/constants/notifications'
import { BaseRoute } from '~/constants/routes'
import {
  fetchConversation,
  setIsReadForConversation,
} from '~/store/actions/conversations'
import { markAllNotificationsAsReadForLinkedItem } from '~/store/actions/notifications'
import {
  getFeatureToggle,
  getNotificationArea,
} from '~/store/reducers/constants'
import {
  getConversationById,
  getConversationMessagesLoading,
  getConversationsValidationError,
} from '~/store/reducers/conversations'
import { replaceHtmlPairedTagWithAnother } from '~/utils'
import {
  getRecipientNameWithCommunicationAddress,
  isPlainTextTransport,
} from '~/utils/communicationsUtils'
import useDialog from '~/utils/useDialog'
import useErrorAlert from '~/utils/useErrorAlert'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import ChatMessage from './message/ChatMessage'
import NoEarlierMessages from './message/NoEarlierMessages'
import { useConversationChat } from './useConversationChat'

const StyledGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
}))

const replacePreTagsWithDiv = (content: string) =>
  replaceHtmlPairedTagWithAnother(content, 'pre', 'div')

export interface ChatProps {
  conversationId: string
  variant?: MessageVariant
}

const Chat = ({
  conversationId,
  variant = MessageVariant.PRIMARY,
}: ChatProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common'])

  const isLogicalSmsThreadingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LOGICAL_SMS_THREADING),
  )
  const NotificationAreas = useSelector(getNotificationArea)
  const communicationsNotificationAreaId = Utils.findConstantIdByName(
    NotificationAreaNames.COMMUNICATIONS,
    NotificationAreas,
  )
  const conversation = useSelector(getConversationById(conversationId))
  const areMessagesLoading = useSelector(getConversationMessagesLoading)

  const [isInitialized, setIsInitialized] = useState(false)
  const scrollContainerRef = useRef<HTMLElement | null>(null)

  const [openDeleteDialog] = useDialog(DialogNames.CONVERSATION_DELETE_DIALOG)

  const targetMessageId = searchParams.get('message')

  const { loadMoreItems, messages } = useConversationChat({
    conversationId,
    targetMessageId,
    isInitialized,
  })

  const isContextItem = useIsCurrentContextItem(conversation)

  const { from: businessName, recipient = {}, transport } = conversation || {}

  const to = recipient.name || t('Common:UNKNOWN')
  const recipientNameWithAddress = getRecipientNameWithCommunicationAddress(
    transport,
    recipient,
  )
  const recipientName =
    variant === MessageVariant.MINI ? to : recipientNameWithAddress

  const chatMessages = messages.map((message) => ({
    ...message,
    content: replacePreTagsWithDiv(message.body),
    sendingDate: message.timestamp,
    creator: Utils.getPersonString(message.creator),
    from: message.way === MessageWay.OUTBOUND ? businessName : recipientName,
    to: message.way === MessageWay.OUTBOUND ? recipientName : businessName,
    transport,
  }))

  const totalCount =
    conversation?.totalCount ?? CONVERSATION_MESSAGES_FIRST_FETCH_COUNT
  const hasMore = (totalCount ?? 0) > messages.length
  const isTargetLoaded =
    Boolean(targetMessageId) &&
    R.find(R.propEq('id', targetMessageId))(messages)
  const shouldAutoLoadTargetMessage =
    Boolean(targetMessageId) && !isTargetLoaded && hasMore
  const invalidTargetId = !isTargetLoaded && !hasMore

  const loader = (
    <StyledGrid container justifyContent="center" key={0}>
      <Text variant="body2">{t('Common:LOADING')}...</Text>
    </StyledGrid>
  )

  const messagesInitialized =
    !R.isNil(conversation?.totalCount) && !areMessagesLoading

  useEffect(() => {
    if (
      messagesInitialized &&
      messages?.length === 0 &&
      !shouldAutoLoadTargetMessage
    ) {
      openDeleteDialog({ conversationId })
    }
  }, [messages?.length, messagesInitialized, shouldAutoLoadTargetMessage])

  useEffect(() => {
    if (targetMessageId && shouldAutoLoadTargetMessage) {
      loadMoreItems()
    }
  }, [targetMessageId, shouldAutoLoadTargetMessage, loadMoreItems])

  // Set isInitialized only when the scroll target is ready:
  // - For targetMessageId: wait until the target message is loaded (or confirmed invalid)
  // - For no target: initialize once messages are initialized (scroll to last)
  useEffect(() => {
    if (isInitialized || !messagesInitialized || messages.length === 0) {
      return
    }

    const shouldInitialize = targetMessageId
      ? Boolean(isTargetLoaded) || invalidTargetId
      : true

    if (shouldInitialize) {
      setIsInitialized(true)
    }
  }, [
    isInitialized,
    messagesInitialized,
    messages.length,
    targetMessageId,
    isTargetLoaded,
    invalidTargetId,
  ])

  useEffect(() => {
    if (
      conversation?.notifications &&
      conversation?.notifications?.length > 0
    ) {
      dispatch(
        markAllNotificationsAsReadForLinkedItem(
          conversationId,
          communicationsNotificationAreaId,
        ),
      )
    }
  }, [conversation?.notifications?.length])

  useEffect(() => {
    if (conversation && conversationId && isContextItem) {
      dispatch(setIsReadForConversation(conversationId, true))
    }
  }, [conversationId, isContextItem])

  useEffect(() => {
    setIsInitialized(false)
  }, [conversationId, targetMessageId])

  useEffect(() => {
    scrollContainerRef.current = document.querySelector(
      '[data-testid="chat-container"]',
    ) as HTMLElement
  }, [])

  useErrorAlert({
    errorSelector: getConversationsValidationError,
    onOpenHandler: () => {
      navigate(BaseRoute.COMMUNICATIONS)
    },
    onCloseHandler: () => {
      dispatch(fetchConversation(conversationId))
    },
  })

  return (
    <InfiniteScroll
      isReverse
      data-testid="chat-container"
      hasMore={hasMore}
      initialLoad={false}
      loadMore={loadMoreItems}
      loader={loader}
      threshold={100}
      useWindow={false}
    >
      {!hasMore && isLogicalSmsThreadingEnabled && <NoEarlierMessages />}
      {chatMessages.map((message, index) => {
        const isLastMessage = chatMessages.length === index + 1
        const scrollToMessage =
          Boolean(targetMessageId) && targetMessageId === message.id
        const scrollToLast = targetMessageId
          ? invalidTargetId && isLastMessage
          : isLastMessage

        return (
          <Fragment key={message.id}>
            {scrollToMessage && (
              <ScrollAnchor
                autoScroll={!isInitialized}
                block="start"
                scrollContainer={scrollContainerRef.current}
                testId="target-message"
              />
            )}
            <ChatMessage
              isPlainTextTransport={isPlainTextTransport(transport)}
              message={message}
              readOnly={!isContextItem}
              variant={variant}
            />
            {scrollToLast && (
              <ScrollAnchor
                autoScroll={!isInitialized}
                scrollContainer={scrollContainerRef.current}
                testId="last-message"
              />
            )}
          </Fragment>
        )
      })}
    </InfiniteScroll>
  )
}

export default Chat
